/**
 * Clinic registration, staff and licence values shared by the Worker and the practitioner pages.
 * Licence checks are pure so a bad number or state is refused without a database.
 */
import type { Actor, ClinicRole } from "./access.ts";

/** US states and DC, the values stored on `practitioner_profile.licence_state`. */
export const LICENCE_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
] as const;

export type LicenceState = (typeof LICENCE_STATES)[number];

export type LicenceStatus = "unverified" | "verified";

export function isLicenceState(value: string): value is LicenceState {
  return LICENCE_STATES.some((state) => state === value);
}

const LICENCE_NUMBER = /^[A-Za-z0-9][A-Za-z0-9-]{0,31}$/;

/** A trimmed licence number, or null when it is empty, too long, or has characters other than letters, digits and hyphens. */
export function parseLicenceNumber(input: string): string | null {
  const value = input.trim();

  return LICENCE_NUMBER.test(value) ? value : null;
}

/** A two-letter state code, uppercased, or null. */
export function parseLicenceState(input: string): LicenceState | null {
  const value = input.trim().toUpperCase();

  return isLicenceState(value) ? value : null;
}

const NAME_MAX = 80;

/** A person's or clinic's name: trimmed, collapsed spaces, 1–80 characters, no control characters. */
export function parseClinicName(input: string): string | null {
  const value = input.trim().replaceAll(/\s+/g, " ");

  if (value.length === 0 || value.length > NAME_MAX) return null;

  for (const char of value) if (char.charCodeAt(0) < 32) return null;

  return value;
}

/**
 * Paths the clinic magic link may return to. Only /clinic and its query, so a callback cannot
 * send the session at an origin we do not control.
 */
export function clinicCallback(input: string | undefined): string {
  const value = (input ?? "").trim();

  if (value === "" || value === "/clinic") return "/clinic";

  if (!value.startsWith("/clinic")) return "/clinic";

  if (value.includes("://") || value.includes("//") || value.includes("\\")) return "/clinic";

  return value;
}

export type ClinicLicence = {
  readonly number: string;
  readonly state: LicenceState;
  readonly status: LicenceStatus;
};

export type ClinicMember = {
  readonly userId: string;
  readonly name: string;
  readonly email: string;
  readonly roles: readonly ClinicRole[];
  readonly licenceStatus: "none" | LicenceStatus;
};

export type ClinicInvitation = {
  readonly id: string;
  readonly email: string;
  readonly roles: readonly ClinicRole[];
  readonly expiresAt: string;
};

/** What an invited person is told before they accept: who invited them and as what. */
export type ClinicInvitationInfo = {
  readonly clinicName: string;
  readonly roles: readonly ClinicRole[];
  readonly email: string;
};

export type ClinicPatient = {
  readonly linkId: string;
  readonly patientId: string;
  readonly name: string | null;
  readonly status: "pending" | "active";
  readonly practitionerUserId: string | null;
  readonly practitionerName: string | null;
};

export type ClinicSnapshot = {
  readonly organization: {
    readonly id: string;
    readonly name: string;
    readonly slug: string | null;
  };
  readonly roles: readonly ClinicRole[];
  readonly canSubmitFormulas: boolean;
  readonly licence: ClinicLicence | null;
  readonly members: readonly ClinicMember[];
  readonly invitations: readonly ClinicInvitation[];
  readonly patients: readonly ClinicPatient[];
};

/** A clinic the caller belongs to, for choosing which one the session works in. */
export type ClinicChoice = {
  readonly id: string;
  readonly name: string;
  readonly roles: readonly ClinicRole[];
};

/**
 * `clinic` is the session's active clinic, null until the caller registers or accepts an
 * invitation, and null for a member of several clinics who has not chosen one. `clinics` lists every
 * clinic the caller belongs to.
 */
export type ClinicHome = {
  readonly clinic: ClinicSnapshot | null;
  readonly clinics: readonly ClinicChoice[];
};

export type ClinicRegistrationRefusal = "staff_account" | "already_registered";

/**
 * Why this person may not register a clinic, or null when they may (PRA-001). Registering makes the
 * person the clinic's owner and practitioner. An account with any platform role is refused because
 * staff verify licences and review the formulas clinics submit (ADM-012), so a staff member who
 * owned a clinic would approve their own clinic's work. A customer may register, since a
 * practitioner can also buy their own formula.
 */
export function clinicRegistrationRefusal(
  person: Pick<Actor, "platformRoles" | "memberships">,
): ClinicRegistrationRefusal | null {
  if (person.platformRoles.length > 0) return "staff_account";

  return person.memberships.length > 0 ? "already_registered" : null;
}
